<script lang="ts">
	import { create } from "@bufbuild/protobuf";
	import { CollisionPriority } from "@dnd-kit/abstract";
	import { KeyboardSensor, PointerSensor } from "@dnd-kit/dom";
	import { DragDropProvider } from "@dnd-kit/svelte";
	import { isSortable } from "@dnd-kit/svelte/sortable";
	import deepEqual from "deep-equal";
	import { getContext } from "svelte";
	import { v4 } from "uuid";
	import { QuestionSchema, type Poll, type Question } from "#gen/forms/v1/poll_pb.js";
	import { DEFAULT_POLL_ANSWER_IMAGE } from "#lib/components/flow/editor/appearance/constants.js";
	import type { DragEndEvent, DragOverEvent } from "#lib/components/flow/editor/types.js";
	import { move } from "#lib/components/flow/editor/types.js";
	import QuestionContainer from "#lib/components/page/components/poll/QuestionContainer.svelte";
	import { setEditorState } from "#lib/components/page/components/poll/utils.js";
	import clickOutside from "#lib/directives/clickOutside.ts";
	import type { Writable } from "svelte/store";

	interface Props {
		poll: Poll;
		onChange?: (poll: Poll) => void;
		onInput?: (poll: Poll) => void;
	}

	const editorState: Writable<Record<string, unknown>> = getContext("editorState");

	let { poll, onChange, onInput }: Props = $props();
	let activeQuestion: string | undefined = $state();
	let pendingScroll: Question | undefined = $state();
	let questions = $derived(poll.questions);

	let snapshot: Question[] = [];

	$effect(() => {
		if (!pendingScroll) {
			return;
		}
		const element = document.getElementById(`question-${pendingScroll.id}`);
		element?.scrollIntoView({ behavior: "smooth", block: "start" });
		pendingScroll = undefined;
	});

	function handleAddQuestion() {
		const newQuestion = create(QuestionSchema, {
			id: v4(),
			name: `Poll ${poll.questions.length + 1}`,
			pollAnswers: [
				{ value: v4(), primaryLabel: "Option 1", image: { ...DEFAULT_POLL_ANSWER_IMAGE } },
				{ value: v4(), primaryLabel: "Option 2", image: { ...DEFAULT_POLL_ANSWER_IMAGE } },
			],
		});
		onChange?.({ ...poll, questions: [...poll.questions, newQuestion] });
		handleSelect(newQuestion, poll.questions.length - 1);
		pendingScroll = newQuestion;
	}

	function handleDuplicate(question: Question) {
		const copy = { ...question, id: v4() };
		poll.questions = [...poll.questions, copy];
		handleChange(poll);
		handleSelect(copy, poll.questions.length - 1);
		pendingScroll = copy;
	}

	function handleQuestionInput(question: Question, index: number) {
		if (index < 0 || index >= poll.questions.length) {
			return;
		}
		poll.questions[index] = question;
		handleInput(poll);
	}

	function handleQuestionChange(question: Question, index: number) {
		if (index < 0 || index >= poll.questions.length) {
			return;
		}
		poll.questions[index] = question;
		handleChange(poll);
	}

	function handleRemove(question: Question, index: number) {
		if (index < 0 || index >= poll.questions.length) {
			return;
		}
		const selected = question.id === activeQuestion;
		const selectedIndex = activeQuestion ? poll.questions.findIndex(q => q.id === activeQuestion) : -1;
		const copy = [...poll.questions];
		copy.splice(index, 1);
		poll.questions = copy;
		handleChange(poll);
		if (selected) {
			const previousIndex = Math.max(0, index - 1);
			handleSelect(poll.questions[previousIndex], previousIndex);
		} else if (selectedIndex > index) {
			handleSelect(poll.questions[selectedIndex - 1], selectedIndex - 1);
		}
	}

	function handleMoveUp(question: Question, index: number) {
		if (index <= 0) {
			return;
		}
		const newQuestions = move(poll.questions, index, index - 1);
		poll.questions = newQuestions;
		handleChange(poll);
		handleSelect(question, index - 1);
		pendingScroll = question;
	}

	function handleMoveDown(question: Question, index: number) {
		if (index >= poll.questions.length - 1) {
			return;
		}
		const newQuestions = move(poll.questions, index, index + 1);
		poll.questions = newQuestions;
		handleChange(poll);
		handleSelect(question, index + 1);
		pendingScroll = question;
	}

	function onDragStart() {
		snapshot = questions.slice();
	}

	function onDragOver(event: DragOverEvent) {
		const { source, target } = event.operation;
		if (isSortable(source) && isSortable(target)) {
			const fromIndex = source.index;
			const toIndex = target.index;
			if (fromIndex !== toIndex) {
				const newQuestions = [...questions];
				const [removed] = newQuestions.splice(fromIndex, 1);
				newQuestions.splice(toIndex, 0, removed);
				questions = newQuestions;
			}
		}
	}

	function onDragEnd(event: DragEndEvent) {
		if (event.canceled || deepEqual(questions, snapshot)) {
			questions = snapshot;
			return;
		}
		const { target } = event.operation;
		if (isSortable(target)) {
			setSelectedQuestion(target.index);
		}
		handleChange({ ...poll, questions });
	}

	function handleInput(poll: Poll) {
		onInput?.(poll);
	}

	function handleChange(poll: Poll) {
		onChange?.(poll);
	}

	function handleSelect(question: Question, index: number) {
		activeQuestion = question.id;
		setSelectedQuestion(index);
	}

	function handleDeselect() {
		activeQuestion = "";
		setSelectedQuestion(0);
	}

	function setSelectedQuestion(index: number) {
		if (!$editorState) {
			return;
		}
		$editorState = setEditorState($editorState, poll, "currentQuestionIndex", index);
	}
</script>

<div class="flex w-full flex-col gap-4" use:clickOutside={handleDeselect}>
	<DragDropProvider sensors={[KeyboardSensor, PointerSensor]} {onDragStart} {onDragOver} {onDragEnd}>
		<QuestionContainer id={poll.id} collisionPriority={CollisionPriority.Low} {questions} {activeQuestion} onAdd={handleAddQuestion} onDuplicate={handleDuplicate} onRemove={handleRemove} onInput={handleQuestionInput} onChange={handleQuestionChange} onSelect={handleSelect} onMoveUp={handleMoveUp} onMoveDown={handleMoveDown} />
	</DragDropProvider>
</div>
